<script lang="ts">

import { Component, Prop, Vue, Watch } from "vue-facing-decorator";
import { Tab } from "@/domain/search/entitites/Tab.enum";
import SpinnerComponent from "@/components/atoms/Spinner.vue";
import IconComponent from "@/components/atoms/Icon.vue";
import TabsComponent from "@/components/search/Tabs.vue";
import ListItemComponent from "@/components/search/ListItem.vue";
import { useSearchResultsStore } from "@/domain/search/application/SearchResults.store";
import {
    AnalyticsEventName,
    CustomEventId, DOMUtil,
    GTMTicketItemFactory,
    GTMUtils,
    IconName,
    ItemListData,
    TicketItem,
    TranslationKey,
    TranslationService
} from "webshop-modules";
import { SearchAppData } from "@/domain/search/entitites/TranslationsData.interface";
import Artist from "./Artist.vue"
import { ITab } from "@/domain/search/entitites/Tab.interface";

enum ScriptType {
    Artist = 'artist',
    Event = 'event',
}

@Component({
    components: {
        Artist,
        SpinnerComponent,
        ListItemComponent,
        IconComponent,
        TabsComponent
    }
})
export default class ResultsComponent extends Vue {

    @Prop()
    public data!: SearchAppData;

    @Watch('searchResultsState.resultList')
    private handleEventResultsChange() {
        if (this.activeTab === Tab.Events) {
            this.sendViewItemListEvent();
        }

        this.insertStructuredDataScriptFor(ScriptType.Event, this.eventsStructuredData);
    }

    @Watch('searchResultsState.artistsList')
    private handleArtistResultsChange() {
        if (this.activeTab === Tab.Artists) {
            this.sendViewItemListEvent();
        }

        this.insertStructuredDataScriptFor(ScriptType.Artist, this.artistsStructuredData);
    }

    public readonly IconName: typeof IconName = IconName;
    public readonly Translation: typeof TranslationKey = TranslationKey;
    public readonly Tab: typeof Tab = Tab;
    public lang: string = TranslationService.getCountryCode() === 'en'
        ? 'en'
        : 'lc';

    public initialTab: Tab = Tab.Events;
    public tabs: Tab[] = [ Tab.Events ];
    public activeTab = this.initialTab;
    public modifier = '-results';

    public mounted() {
        this.tabs = this.setupTabsFor(this.data);
    }

    public readonly searchResultsStore = useSearchResultsStore();

    public get results(): any[] {
        return this.searchResultsStore.resultList.results ?? [];
    }

    public get artists(): any[] {
        return this.searchResultsStore.artistsList.results ?? [];
    }

    public get numOfEvents(): number | undefined {
        return this.searchResultsStore.resultList.totalCount;
    }

    public get numOfArtists(): number | undefined {
        return this.searchResultsStore.artistsList.totalCount;
    }

    public get haveMoreEvents(): boolean {
        return this.searchResultsStore.haveMoreEvents;
    }

    public get haveMoreArtists(): boolean {
        return this.searchResultsStore.haveMoreArtists;
    }

    public get isLoading(): boolean {
        return this.searchResultsStore.inProgress;
    }

    public get canLoadMore(): boolean {
        if (this.activeTab === Tab.Artists) {
            return this.searchResultsStore.haveMoreArtists && !this.isLoading;
        } else {
            return this.searchResultsStore.haveMoreEvents && !this.isLoading;
        }
    }

    public handleTabChange(tab: Tab) {
        this.activeTab = tab;

        this.handleAnalyticsViewEvent();
    }

    public tabHeadSlotName(tabName: Tab) {
        return `a-tabHead-${tabName}`;
    }

    public tabPanelSlotName(tabName: Tab) {
        return `a-tabPanel-${tabName}`;
    }

    public noEvents() {
        return this.results.length === 0 && !this.isLoading && this.activeTab === Tab.Events;
    }

    public translationForTabName(name: string) {
        switch (name) {
            case 'events':
                return {key: this.Translation.Navigation_section_events};
            case 'artists':
                return {key: this.Translation.Navigation_section_artists};
            case 'venues':
                return {key: this.Translation.Navigation_section_venues};
        }
    }

    public get eventsStructuredData() {
        let dataObject: any = [];

        if (this.results) {
            for (let event of this.results) {
                dataObject.push({
                    '@context': 'https://schema.org',
                    '@type': 'EventSeries',
                    '@id': event.url,
                    'name': event.title,
                    'url': event.url,
                    'image': DOMUtil.getWebshopStaticPathFrom(document) + event.image
                });
                for (let item of event.events!) {
                    dataObject.push({
                        "@context": "http://schema.org",
                        "@type": "Event",
                        "eventStatus": "http://schema.org/EventScheduled",
                        "location": {
                            "@type": "EventVenue",
                            "name": item.venue,
                            "address": {
                                "@type": "PostalAddress",
                                "addressLocality": item.street + ', ' + item.city,
                                "addressCountry": this.data!.countryTranslations[this.lang][item.lkz!]
                            },
                        },
                        "startDate": (item.dateStart as any).dateTime,
                        "endDate": (item.dateEnd as any).dateTime,
                        "superEvent": event.url,
                    })
                }
            }
        }

        return dataObject;
    }

    public get artistsStructuredData() {
        let dataObject: any = [];

        if (this.artists) {
            for (let artist of this.artists) {
                if (artist.image) {
                    dataObject.push({
                        '@context': 'https://schema.org',
                        '@type': 'PerformingGroup',
                        'name': artist.title,
                        'url': artist.url,
                        'image': DOMUtil.getWebshopStaticPathFrom(document) + artist.image
                    });
                } else {
                    dataObject.push({
                        '@context': 'https://schema.org',
                        '@type': 'PerformingGroup',
                        'name': artist.title,
                        'url': artist.url,
                    });
                }

            }
        }

        return dataObject;
    }

    public loadMore() {
        if (this.activeTab === Tab.Artists) {
            this.searchResultsStore.artistsNextPage(this.$apiService);
        }

        if (this.activeTab === Tab.Events) {
            this.searchResultsStore.eventsNextPage(this.$apiService);
        }
    }

    public getListName() {
        return this.searchResultsStore.params.searchText && this.searchResultsStore.params.searchText.length > 0 ?
            `search_result_${this.activeTab}_` + this.searchResultsStore.params.searchText
            : `search_result_${this.activeTab}_all`;
    }

    public getTabConfiguration(): ITab[] {
        return this.tabs.map(tabName => ({
            name: tabName,
            badgeValue: tabName === Tab.Events ? this.numOfEvents : this.numOfArtists,
            translationObject: this.translationForTabName(tabName),
        }));
    }

    private handleAnalyticsViewEvent() {
        if (!this.isLoading) {
            this.sendViewItemListEvent();
        } else {
            const unwatch = this.$watch('isLoading', (newValue) => {
                if (newValue === false) {
                    this.sendViewItemListEvent();
                    unwatch();
                }
            });
        }
    }

    private sendViewItemListEvent() {
        const items = this.results
            .map((item, index) => GTMTicketItemFactory.fromSearchResult(item, index, this.getListName()))
            .filter(item => !!item) as TicketItem[];

        const eventData = {
            event: AnalyticsEventName.ViewItemList,
            ecommerce: {
                item_list_name: this.getListName(),
                item_list_id: this.getListName(),
                items,
            }
        }

        if (window['DWS']['isDomReady']) {
            GTMUtils.pushGA4EcommerceToDataLayer<ItemListData>(eventData);
            GTMUtils.storeItemList(items);
        } else {
            window.addEventListener(CustomEventId.GTMDomReady, () => {
                GTMUtils.pushGA4EcommerceToDataLayer<ItemListData>(eventData);
                GTMUtils.storeItemList(items);
            });
        }

    }

    private insertStructuredDataScriptFor(type: ScriptType, data: any) {
        const id = `${type}-structured-data-script`;
        const existingScript = document.getElementById(id);
        if (existingScript) {
            existingScript.remove();
        }

        const script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        script.text = JSON.stringify(data);

        document.head.appendChild(script);
    }

    private setupTabsFor(data: SearchAppData): Tab[] {
        const collection: Tab[] = [ Tab.Events ];

        if (data.showArtistResults) {
            collection.push(Tab.Artists);
        }

        return collection;
    }
}
</script>

<template>
    <div id="results-component" class="l-container l-bg-primary">

        <TabsComponent
            v-bind:tabs="getTabConfiguration()"
            v-bind:initialTab="initialTab"
            v-bind:modifier="modifier"
            @activeTabChange="handleTabChange">

            <template v-slot:[tabPanelSlotName(activeTab)]>
                <div class="m-blockNotice" v-if="noEvents()">
                    <span v-translate="{key: Translation.No_search_results_notice}"></span>
                </div>

                <template v-if="results && activeTab === Tab.Events">
                    <ul class="search-results-list">
                        <ListItemComponent v-for="(event, index) in results"
                                        :data="data"
                                        :index="index"
                                        :item="event"
                                        :listName="getListName()"
                        ></ListItemComponent>
                    </ul>
                </template>

                <template v-if="artists && activeTab === Tab.Artists">
                    <ul class="search-results-list">
                        <Artist v-for="artist in artists"
                                :data="data"
                                :item="artist"
                        ></Artist>
                    </ul>
                </template>

                <SpinnerComponent v-if="isLoading" label=""></SpinnerComponent>

                <div class="m-searchList__loadMore" v-show="canLoadMore">
                    <button class="a-btnCta -action"
                            @click="loadMore"
                            v-translate="{ key: Translation.More_search_results}"
                    ></button>
                </div>
            </template>
        </TabsComponent>
    </div>
</template>
